/*
 * 戒导航 · 弹窗关闭守卫（jiedh/modal-guard）前台逻辑
 * =============================================================================
 * 一、背景（用户 2026-10-15 报障 + 开发环境真实浏览器取证）
 * -----------------------------------------------------------------------------
 *   复现路径：论坛帖子里回帖 → 上传附件 → 编辑框清空 → 个人媒体中心重新选附件 →
 *             点「插入」→ **弹窗（设置下载名称）不消失，且可以一直点一直插入**。
 *
 *   根因不在 fof/upload 的业务代码，而在 Flarum 核心 ModalManager 的一把布尔锁：
 *   vendor/flarum/core/js/src/common/components/ModalManager.tsx 的 animateHide()：
 *
 *       animateHide(closedCallback = () => {}) {
 *         if (this.modalClosing) return;        // ← 第 197 行：锁着就直接返回（什么都不做）
 *         this.modalClosing = true;             // ← 第 198 行：上锁
 *         const afterModalClosedCallback = () => {
 *           this.modalClosing = false;          // ← 解锁
 *           this.attrs.state.close();           // ← 真正把弹窗从 modalList 里移除
 *           closedCallback();
 *         };
 *         this.activeDialogElement?.addEventListener('transitionend', afterModalClosedCallback, { once: true });
 *         this.activeDialogElement?.classList.remove('in');
 *         this.activeDialogElement?.classList.add('out');   // ← 触发 200ms 退场过渡
 *       }
 *
 *   关键：**解锁 100% 依赖 transitionend**，即那个弹窗元素必须真的把 0.2s 过渡动画播完。
 *
 *   而 fof/upload 的 FileManagerModal.onSelect()（个人媒体中心里点「选择文件」）是
 *   「先 hide() 自己、紧接着 show() 下一个弹窗」：
 *
 *       this.hide();                            // 给自己的元素加 .out、把 modalClosing 置 true
 *       insertWithDisplayName(files[0], ...);   // → app.modal.show(DisplayNameModal)
 *
 *   app.modal.show() 默认是**替换**（stackModal=false，见 ModalManagerState.show() 第 112-115 行
 *   `this.modalList = [this.modal]`）。于是旧弹窗在下一帧就被从 modalList 里换掉 → Mithril 移除它，
 *   而 Modal.onbeforeremove()（Modal.tsx 第 68-80 行）发现 `this.attrs.state.modal` 已指向新弹窗（真值），
 *   直接 `return undefined` → **立刻摘除 DOM，不等动画**。
 *   元素没了 → transitionend 永不触发 → **modalClosing 永久卡在 true**。
 *
 *   之后「设置下载名称」弹窗每次点「插入」：onsubmit() 先执行真正的插入，再 this.hide()；
 *   而 hide() → animateHide() 在第 197 行被那把陈旧的锁拦掉 → 弹窗不关、插入却照做。
 *   → 完全吻合「弹窗不消失、可以一直插入附件」。
 *
 * 二、本扩展做什么
 * -----------------------------------------------------------------------------
 *   在 animateHide 真正执行前做一次「陈旧锁体检」：
 *     若 modalClosing 已是 true，但页面上**根本查不到**正在退场的弹窗元素
 *     （`.ModalManager .Modal.out` 为空），说明这把锁对应的元素早已被摘除、
 *     transitionend 永远等不到 —— 判定为「陈旧锁」，就地复位为 false，让本次关闭正常进行。
 *
 *   判据为什么安全（已逐一推演 + 浏览器探针实测）：
 *     · 正常关闭中：核心刚给元素加了 `.out`，必然能查到 → 不复位，行为与原生完全一致；
 *     · 连点两次关闭：第二次仍能查到 `.out` → 照旧被核心自身的锁挡住 → 不会「双关闭」；
 *     · 陈旧锁场景：旧弹窗已被摘除，查不到 `.out`（探针实测 domOut=false）→ 复位 → 正常关闭。
 *   即：**只在「这把锁已经不可能自解锁」时复位**，不触碰任何正常路径。
 *
 * 三、为什么用「运行时包装原型方法」而不是改 vendor/
 * -----------------------------------------------------------------------------
 *   项目硬约束：vendor/ 是 composer 管理的核心，升级时必须保持原样。
 *   包装的最大风险是核心将来改了实现 —— 届时本扩展最多退化成「不修复」，
 *   绝不会报错、不会影响正常弹窗（全部分支都有 try/catch 与类型判断兜底）。
 *
 * 四、为什么要用「字符串路径」而不是 import ModalManager
 * -----------------------------------------------------------------------------
 *   与 packages/login-guard/js/src/forum/index.ts 同一套理由：核心的 JS 模块注册在
 *   flarum.reg 里（flarum-webpack-config 会把 `import 'flarum/...'` 编译成 flarum.reg.get）。
 *   用**字符串路径**调用 override()，核心源码 common/extend.ts（第 107-113 行）会走
 *   `flarum.reg.onLoad(namespace, id, (module) => override(module.prototype, ...))` 分支：
 *   模块已加载就立刻生效，尚未加载就排队等 chunk 到位后自动生效 —— 与加载顺序无关。
 *   因此本文件**看不到任何 ModalManager 的 import**，这不是遗漏，而是刻意为之。
 *
 * 数据依据：core 模块注册名 'core' / 'common/components/ModalManager'
 * （命名空间与 id 由 common/ExportRegistry.ts 的 namespaceAndIdFromPath() 从
 *  'flarum/common/components/ModalManager' 解析得到，已在浏览器实测 flarum.reg.get 可取到）。
 */
import app from 'flarum/forum/app';
import { override } from 'flarum/common/extend';

/**
 * 核心 ModalManager 的注册表路径（字符串形式）。
 * 会被 common/extend.ts 的 namespaceAndIdFromPath() 解析成
 * namespace='core'、id='common/components/ModalManager'。
 */
const MODAL_MANAGER_PATH = 'flarum/common/components/ModalManager';

/**
 * 「正在退场的弹窗」选择器。
 *
 * · `.ModalManager`：核心为每个弹窗套的外层容器（ModalManager.tsx 第 47 行 className）。
 * · `.Modal`：弹窗本体（Modal.tsx 的 className() 返回值里必有 Modal）。
 * · `.out`：只有 animateHide() 会给它加（ModalManager.tsx 第 212 行），
 *   配合 less/common/Modal.less 的 0.2s 过渡；`.in` 是显示态。
 * 三者组合即「此刻页面上确实有一个弹窗正在播退场动画」。
 */
const CLOSING_MODAL_SELECTOR = '.ModalManager .Modal.out';

/**
 * 打开详细日志（排查期可置 true）。
 * 平时保持 false：本扩展的复位动作每次关闭都可能发生，日志会刷屏。
 */
const VERBOSE = false;

/**
 * 判断此刻页面上是否真的有弹窗正在播放退场动画。
 *
 * 用 document.body 作为查询根，与核心 activeDialogElement 的实现保持一致
 * （ModalManager.tsx 第 169-171 行同样从 document.body 起查）。
 *
 * @returns true = 确有 `.out` 弹窗在动画中（这把锁是「活的」，不能动）；
 *          false = 页面上没有任何正在退场的弹窗（锁可能是陈旧的）。
 */
function isAnyModalAnimatingOut() {
  try {
    return document.body.querySelector(CLOSING_MODAL_SELECTOR) !== null;
  } catch (e) {
    // 理论上 querySelector 不会抛；万一抛了，保守地认为「有动画在进行」，
    // 即**不**去复位那把锁，绝不冒然干预核心行为。
    return true;
  }
}

/**
 * 安装「弹窗关闭守卫」。
 *
 * 包装核心 ModalManager.prototype.animateHide：
 *   进入原实现之前，若发现「锁已置位，但页面上没有任何正在退场的弹窗」，
 *   就复位 modalClosing，让这次关闭能正常走完（重新加 .out、等 transitionend、state.close()）。
 */
export default function installModalCloseGuard() {
  override(MODAL_MANAGER_PATH, 'animateHide', function (original) {
    for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
      args[_key - 1] = arguments[_key];
    }
    try {
      // 仅在「锁已置位 + 页面上没有任何弹窗正在退场」时复位。
      // 该判据等价于「这把锁对应的元素早已被摘除，transitionend 永远不会来」。
      if (this.modalClosing === true && !isAnyModalAnimatingOut()) {
        this.modalClosing = false;
        if (VERBOSE) {
          console.info('[jiedh/modal-guard] 检测到陈旧的弹窗关闭锁（modalClosing=true 但无 .out 弹窗），已复位。');
        }
      }
    } catch (e) {
      /* 忽略：守卫本身绝不能影响弹窗的正常关闭流程 */
    }

    // 原样交回核心实现（含可能的 closedCallback 参数转发）。
    // 注意：override() 传入的 original 已由核心 bind 到当前实例，直接调用即可。
    if (typeof original !== 'function') return;
    return original(...args);
  });
}

// 初始化器 name 必须与扩展 ID 对应（本包 ID 为 jiedh/modal-guard → CLI 形式 jiedh-modal-guard）。
// 不设置优先级：本扩展与其它扩展互不影响，使用默认顺序即可。
app.initializers.add('jiedh-modal-guard', () => {
  installModalCloseGuard();
});